import React, { useState, useEffect } from 'react';
import { FaCoins, FaStopwatch, FaClock, FaAd, FaSave, FaLock, FaUnlock } from 'react-icons/fa';
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import Navbar from '../../Quiz/Navbar';
import Loader from '../../../components/Loader';

const AdsEarn = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({
        "RW_coin": 5,
        "FS_coin": 10,
        "NTV_coin": 3,
        "InHouse_coin": 7,
        "sub_limit": 2,
        "release_time": 15,
        "main_limit": 100
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const resp = await Calling.getAdsSetting();
            if (resp?.status) {
                setData(resp.data);
                setError(null);
            } else {
                setError(resp?.message || 'Failed to fetch ads settings');
                toast.error(resp?.message || 'Failed to fetch ads settings');
            }
        } catch (error) {
            console.error(error);
            setError('Failed to fetch ads settings. Please try again.');
            toast.error('Failed to fetch ads settings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Convert string values to numbers and ensure minimum of 1
        const numValue = parseInt(value, 10);
        const validValue = isNaN(numValue) ? 1 : Math.max(1, numValue);

        setData({
            ...data,
            [name]: validValue
        });
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        // Validation
        const invalidFields = Object.entries(data).filter(([key, value]) => value < 1);
        if (invalidFields.length > 0) {
            const fieldNames = invalidFields.map(([key]) => key).join(', ');
            setError(`All values must be at least 1. Please check: ${fieldNames}`);
            toast.error(`All values must be at least 1. Please check: ${fieldNames}`);
            setSaving(false);
            return;
        }

        try {
            const resp = await Calling.updateAdsSetting(data);

            if (resp?.status) {
                setSuccess(resp?.message || "Ads settings updated successfully!");
                toast.success(resp?.message || "Ads settings updated successfully!");
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(resp?.message || "Failed to update ads settings.");
                toast.error(resp?.message || "Failed to update ads settings.");
            }
        } catch (error) {
            console.error(error);
            setError("Failed to update ads settings. Please try again.");
            toast.error("Failed to update ads settings. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader loading={loading} />
            </div>
        );
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Ads Earn Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'adsEarn'} />
                <hr className='border-main-border' />

                <div className="rounded-xl p-5">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-main-delete rounded-md border border-red-200">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
                            {success}
                        </div>
                    )}

                    <div className="bg-main-bg rounded-xl p-5 border border-main-border">
                        <h3 className="text-lg font-semibold mb-4 text-main-font">Coin Rewards Configuration</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-main-font mb-2" htmlFor="RW_coin">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> Rewarded Video Coins
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="RW_coin"
                                    name="RW_coin"
                                    value={data.RW_coin}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coins awarded for watching rewarded videos</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="FS_coin">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> (Full Screen / Open) Coins
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="FS_coin"
                                    name="FS_coin"
                                    value={data.FS_coin}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coins awarded for full screen ads</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="NTV_coin">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> Native Coins
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="NTV_coin"
                                    name="NTV_coin"
                                    value={data.NTV_coin}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coins awarded for native ads</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="InHouse_coin">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> In-House Coins
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="InHouse_coin"
                                    name="InHouse_coin"
                                    value={data.InHouse_coin}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coins awarded for in-house promotions</p>
                            </div>
                        </div>

                        <h3 className="text-lg font-semibold mb-4 text-main-font mt-8">Limits and Timing Settings</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-main-font mb-2" htmlFor="sub_limit">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> Sub Limit Coin
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="sub_limit"
                                    name="sub_limit"
                                    value={data.sub_limit}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coin limit per submission</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="release_time">
                                    <div className="flex items-center">
                                        <FaUnlock className="mr-2 text-main-blue" /> Unlock Time (minutes)
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="release_time"
                                    name="release_time"
                                    value={data.release_time}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Unlock waiting time in minutes</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="main_limit">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> Max Limit Coin
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="main_limit"
                                    name="main_limit"
                                    value={data.main_limit}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Maximum coin limit per day</p>
                            </div>
                        </div>

                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className={`px-6 py-2.5 rounded-lg text-white ${saving ? 'bg-opacity-70' : 'hover:bg-[#5A3DEE] transform hover:scale-[1.02]'
                                    } bg-main-blue transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-main-blue flex items-center`}
                            >
                                {saving ? (
                                    <>
                                        <span className="mr-2 inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="mr-2" />
                                        Save Settings
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdsEarn;